import { Button, H2, Modal, TextInput } from '@manulife/mux';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import EmailModalLoadingIndicator from '../LoadingIndicator/EmailModalLoadingIndicator';
import { EmailButtonWrapper, ErrorMsg } from '../Shared/SharedStyles';
import { UpdateMemberEmailAddress } from './ReducerActions/UpdateMemberEmailAddress';
import { ADOBE } from '../../utils/AppConstants';

const EmailAddressModal = () => {
    const {
        isLoadingFeatures,
        isLoadingMemberData,
        member,
        isSavingEmailAddress,
        error,
        isCsr,
        features,
        isCiamSession,
        isSteppedUp,
    } = useShallowEqualSelector((state) => ({
        isLoadingFeatures: state.Features.isLoading,
        isLoadingMemberData: state.MemberData.isLoadingMemberData,
        member: state.MemberData.member,
        isSavingEmailAddress: state.MemberData.isSavingEmailAddress,
        error: state.Errors.emailAddressUpdate,
        isCsr: state.Authentication.isCsr,
        features: state.Features.data,
        isCiamSession: state.Authentication.isCiamSession,
        isSteppedUp: state.Authentication.isSteppedUp,
    }));

    const emailAddressLabel = useAEMContent(FRAGMENT_MP.email, 'emailAddressLabel');
    const myEmailAddressLabel = useAEMContent(FRAGMENT_MP.email, 'header');

    const cancelButton = useAEMContent(FRAGMENT_MP.common, 'cancelButton');
    const updateButton = useAEMContent(FRAGMENT_MP.common, 'updateButton');

    const emailBlank = useAEMContent(FRAGMENT_MP.validationMessages, 'emailBlank');
    const emailInvalid = useAEMContent(FRAGMENT_MP.validationMessages, 'emailInvalid');

    const history = useHistory();
    const dispatch = useDispatch();

    const validationSchema = Yup.object().shape({
        emailAddress: Yup.string().email(emailInvalid).required(emailBlank),
    });
    const { handleSubmit, values, errors, setFieldValue } = useFormik({
        initialValues: {
            emailAddress: member.emailAddress ? member.emailAddress : '',
        },
        enableReinitialize: true,
        onSubmit: async () => {
            const response = await dispatch(UpdateMemberEmailAddress(values.emailAddress));

            if (response.success) {
                history.push('/gb/member-profile-2/personal');
            }
        },
        validationSchema,
    });
    const sendAdobeAnalyticsTransaction = (transactionName) => {
                        window.adobeDataLayer?.push({
                            event: ADOBE.TRANSACTION_COMPLETE,
                            transaction: {
                                transactionName,
                                transactionCategory: 'Member Profile',
                            }
                        });
                    };

    useEffect(() => {
        if (isCiamSession && !isSteppedUp) {
            history.push('/gb/member-profile-2/personal');
        }
    }, [history, isCiamSession, isSteppedUp]);

    return (
        <Modal
            ariaLabelledBy="heading"
            id="editEmailAddressModal"
            isOpen={true}
            onClose={() => history.push('/gb/member-profile-2/personal')}
        >
            {(isLoadingFeatures || isLoadingMemberData) && (
                <EmailModalLoadingIndicator data-testid="emailAddressModalLoadingIndicator" />
            )}
            {!isLoadingFeatures && !isLoadingMemberData && (
                <form onSubmit={handleSubmit}>
                    <H2 id="heading" customStyle={{ elementStyle: { marginTop: '0' } }}>
                        {myEmailAddressLabel}
                    </H2>
                    {error?.hasError && (
                        <ErrorMsg>
                            {error.message} Code: {error.code}
                        </ErrorMsg>
                    )}
                    <TextInput
                        id="emailAddress"
                        label={emailAddressLabel}
                        placeholder="name@example.com"
                        customStyle={{
                            rootStyle: {
                                width: '100%',
                            },
                        }}
                        value={values.emailAddress}
                        onChange={(newValue) => setFieldValue('emailAddress', newValue)}
                        disabled={!features['emailAddress']}
                        errors={[
                            {
                                id: 'emailAddress',
                                msg: errors.emailAddress,
                                isError: Boolean(errors.emailAddress),
                            },
                        ]}
                        ariaRequired
                    />
                    <EmailButtonWrapper>
                        <Button
                            id="cancelEmailAddressButton"
                            variant={Button.VARIANT.SECONDARY}
                            onClick={() => {
                                sendAdobeAnalyticsTransaction('Edit My Personal Information');
                                history.push('/gb/member-profile-2/personal');
                            }}
                        >
                            {cancelButton}
                        </Button>
                        <Button
                            id="updateEmailAddressButton"
                            type="submit"
                            disabled={
                                Boolean(isSavingEmailAddress) ||
                                Boolean(errors.emailAddress) ||
                                Boolean(isCsr) ||
                                !features['emailAddress']
                            }
                            saving={isSavingEmailAddress}
                            onClick={() => sendAdobeAnalyticsTransaction('Edit My Personal Information')
                            }
                        >
                            {updateButton}
                        </Button>
                    </EmailButtonWrapper>
                </form>
            )}
        </Modal>
    );
};

export default EmailAddressModal;
